import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Plus,
  Download,
  Building2,
  Globe,
  CheckCircle2,
  XCircle,
  Sparkles,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";

import {
  getAddressBookList,
  DeleteCompanyAddress,
} from "@/redux/Address/actions";
import { callLoader } from "@/redux/Loader/actions";
import Swal from "sweetalert2";
import Pagination from "@/pages/PortPlace/Pagination";
import { getAddressColumns } from "./AddressColumns";
import { DataTable } from "../ui/table";
import { useDebounced } from "@/hooks/useDebounced";
import AIInsightCard from "@/components/shipment/AIInsightCard";

const AddressBook = ({ onCreateNew, onViewShipment }) => {
  const [searchText, setSearchText] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLetter, setSelectedLetter] = useState("");
  const [recordsPerPage, setRecordPerPage] = useState(10);
  const dispatch = useDispatch();

  const { addressList, pagination } = useSelector((state) => state.addressBook);

  const debouncedSearch = useDebounced(() => {
    _getList(1);
  }, 300);

  useEffect(() => {
    if (searchText === null) return;
    debouncedSearch(searchText);
  }, [searchText]);

  useEffect(() => {
    _getList(1);
  }, [recordsPerPage, selectedLetter, searchField]);

  const changePage = (page) => {
    _getList(page);
  };

  const authUser = JSON.parse(localStorage.getItem("authUser"))?.[0];

  const _getList = (page = 1) => {
    setCurrentPage(page);
    dispatch(callLoader(1));

    // ✅ Clear the list before every fetch to prevent accumulation
    dispatch({ type: 'RESET_ADDRESS_LIST' });  // add this action to your reducer too

    try {
      const payload = {
        IPageNo: page,
        IPageRecords: recordsPerPage,
        SearchText: searchText || "",
        SalesPersonId: 0,
        PrefixLetter:
          selectedLetter && selectedLetter !== "ALL" ? selectedLetter : "",
        compnayId: authUser?.companyId,
        Status: searchField !== "all" ? searchField.toUpperCase() : "",
      };
      dispatch(getAddressBookList(payload));
    } catch (error) {
      console.error("Error fetching address book list:", error);
    } finally {
      dispatch(callLoader(0));
    }
};

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This address will be deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });
    if (!result.isConfirmed) return;
    const updateDetails = await DeleteCompanyAddress(id);
    if (
      updateDetails?.statuscode === 201 ||
      updateDetails?.statuscode === 200
    ) {
      toast.success("Address deleted successfully");
      _getList(1);
    }
  };

  const alphabet = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
    "ALL",
  ];

  const columns = getAddressColumns({ onViewShipment, handleDelete });

  const headingText = "Address Book";
  const totalCompanies = pagination?.totalRecord ?? 0;
  const activeCount = addressList.filter((c) => c?.status === "ACTIVE").length;
  const inactiveCount = addressList.filter(
    (c) => c?.status === "INACTIVE",
  ).length;
  const ediCount = addressList.filter((c) => c?.accountNumber !== "NA").length;

  const STATUS_FILTERS = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-6 space-y-6"
    >
      {/* ── Header card — exact RFQ style ──────────────────────────────────── */}
      <div
        className="p-2 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900 shadow-sm dark:shadow-gray-900/40"
      >
        {/* Title row */}
        <div
          className="p-4 border-b border-slate-200 dark:border-slate-800
                        flex items-center gap-4 mb-4"
        >
          {/* ── Icon — blue-600 → indigo-600 ── */}
          <div
            className="shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl
                          bg-gradient-to-br from-blue-600 to-indigo-600 text-white
                          flex items-center justify-center"
          >
            <Building2 className="w-5 h-5" strokeWidth={2.2} />
          </div>

          {/* Title block */}
          <div className="flex-1 min-w-0">
            <div
              className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase
                            tracking-[0.2em] text-blue-600 dark:text-blue-400"
            >
              <Sparkles className="w-3 h-3" strokeWidth={2.5} />
              Address Management
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 8, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="mt-1.5 text-[22px] font-bold text-slate-900 dark:text-white tracking-tight leading-tight"
            >
              <motion.span
                className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                initial={{ width: "0ch" }}
                animate={{
                  width: [
                    `0ch`,
                    `${headingText.length}ch`,
                    `${headingText.length}ch`,
                    "0ch",
                  ],
                }}
                transition={{
                  duration: 4.8,
                  times: [0, 0.22, 0.9, 1],
                  ease: ["linear", "linear", "easeInOut"],
                  repeat: Infinity,
                }}
              >
                {headingText}
              </motion.span>
            </motion.h2>
            <div className="mt-1 text-[12.5px] text-slate-500 dark:text-slate-400">
              Manage your company contacts and shipping addresses
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 shrink-0">
            {/* ── Add button — blue-600 → indigo-600 ── */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCreateNew}
              className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg
                         bg-gradient-to-br from-blue-600 to-indigo-600 text-white
                         text-xs font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Company</span>
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg
                         border border-slate-200 dark:border-slate-700
                         text-slate-600 dark:text-slate-300
                         hover:bg-slate-50 dark:hover:bg-slate-800
                         text-xs font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </motion.button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <AIInsightCard
            icon={Building2}
            title="Total Companies"
            value={totalCompanies}
            variant="compact"
            accentKey="blue"
          />
          <AIInsightCard
            icon={CheckCircle2}
            title="Active"
            value={activeCount}
            variant="compact"
            accentKey="green"
          />
          <AIInsightCard
            icon={XCircle}
            title="Inactive"
            value={inactiveCount}
            variant="compact"
            accentKey="red"
          />
          <AIInsightCard
            icon={Globe}
            title="With EDI"
            value={ediCount}
            variant="compact"
            accentKey="amber"
          />
        </div>
      </div>

      {/* ── Filter card ────────────────────────────────────────────────────── */}
      <div
        className="p-2 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-800
                      bg-white dark:bg-gray-900 shadow-sm dark:shadow-gray-900/40"
      >
        <div className="p-4 space-y-3">
          {/* Status pills + search */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* ── Status pills — blue-600 → indigo-600 ── */}
            <div className="flex items-center gap-1.5">
              {STATUS_FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => {
                    setSearchField(value);
                    setCurrentPage(1);
                  }}
                  className={`h-8 px-3 rounded-lg text-xs font-semibold transition-all ${
                    searchField === value
                      ? "bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm"
                      : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, country..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700
                           bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>
          </div>

          {/* ── Alphabet filter — blue-600 → indigo-600 for active ── */}
          <div
            className="flex flex-wrap gap-1 p-2 sm:p-3
                          bg-slate-50 dark:bg-slate-800/60 rounded-xl"
          >
            {alphabet.map((letter) => {
              // ✅ active check: '' or 'ALL' both highlight the ALL button
              const isActive =
                letter === "ALL"
                  ? !selectedLetter || selectedLetter === "ALL"
                  : selectedLetter === letter;

              return (
                <button
                  key={letter}
                  onClick={() => {
                    setSelectedLetter(letter === "ALL" ? "" : letter);
                    setCurrentPage(1);
                  }}
                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-[11px] sm:text-xs font-semibold
                              transition-all flex-shrink-0 ${
                                isActive
                                  ? "bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm"
                                  : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500"
                              }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Table — RecentFilingsTable style ───────────────────────────────── */}
      <section
        className="rounded-2xl bg-white dark:bg-slate-900
                          border border-slate-200 dark:border-slate-700
                          shadow-sm dark:shadow-slate-900/40
                          overflow-hidden"
      >
        {/* Table header bar */}
        <div
          className="px-5 py-4 border-b border-slate-100 dark:border-slate-700
                        flex flex-wrap items-center justify-between gap-3"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600
                            text-white flex items-center justify-center"
            >
              <ListChecks className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-slate-900 dark:text-white">
                Company Address Book
              </div>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400">
                {totalCompanies} records · live data
              </div>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onCreateNew}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg
                       border border-slate-200 dark:border-slate-700
                       text-[12px] font-semibold text-slate-700 dark:text-slate-300
                       hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add New
          </motion.button>
        </div>

        {/* DataTable styled to match RecentFilingsTable exactly */}
        <div className="overflow-auto max-h-[520px]">
          <DataTable
            data={addressList || []}
            columns={columns}
            boxClass=""
            htrClass="sticky top-0 z-10
                      bg-slate-100/60 dark:bg-slate-800/70
                      backdrop-blur
                      text-[10.5px] uppercase tracking-[0.14em]
                      text-slate-500 dark:text-slate-400"
            thClass="text-left font-semibold px-5 py-3 whitespace-nowrap"
            trClass="border-t border-slate-100 dark:border-slate-700/50
                     transition-colors hover:bg-blue-50/30 dark:hover:bg-blue-900/10
                     odd:bg-transparent even:bg-slate-100/20 dark:even:bg-slate-800/30"
            tdClass="px-5 py-3 text-[12.5px] text-slate-700 dark:text-slate-300"
          />
        </div>
      </section>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      <Pagination
        className="border-t border-slate-200 dark:border-slate-800 pl-2 pr-16 pt-4 pb-8
                   bg-slate-50 dark:bg-slate-800/50 mt-0"
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        setRecordsPerPage={(size) => setRecordPerPage(size)}
        onPrevious={changePage}
        onNext={changePage}
        totalRecord={pagination?.totalRecord}
        handleGoto={changePage}
        title="address book"
      />
    </motion.div>
  );
};

export default AddressBook;
